import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Select,
  InputNumber,
  Switch,
  Button,
  message,
  Alert,
  Divider,
  Space,
  Tag,
  Table,
  Input,
  Popconfirm,
  Row,
  Col,
  Tooltip,
  Grid,
  List,
  Modal
} from 'antd';
import {
  DatabaseOutlined,
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import CachePresetTexts from './CachePresetTexts';

const { Option } = Select;
const { useBreakpoint } = Grid;

// 默认Cache Key设置
const DEFAULT_CACHE_KEY = {
  vary_by_device: false,
  vary_by_country: false,
  vary_by_language: false,
  cache_host_mode: 'original',
  vary_headers: []
};

// 历史库值 1/enable/disable 归一为表单三档(否则下拉显示原始值)
const normSwitch = (v) => {
  const s = String(v ?? '').toLowerCase();
  if (['on', '1', 'true', 'enable', 'enabled'].includes(s)) return 'on';
  if (['off', '0', 'false', 'disable', 'disabled'].includes(s)) return 'off';
  return 'auto';
};

// 预定义的文件类型缓存规则
const DEFAULT_FILE_TYPE_RULES = [
  { extensions: 'jpg,jpeg,png,gif,webp,ico,svg', name: '图片', ttl: 604800, enabled: true },
  { extensions: 'css', name: 'CSS样式', ttl: 604800, enabled: true },
  { extensions: 'js', name: 'JavaScript', ttl: 604800, enabled: true },
  { extensions: 'woff,woff2,ttf,eot,otf', name: '字体文件', ttl: 2592000, enabled: true },
  { extensions: 'mp4,webm,ogg,mp3,wav', name: '媒体文件', ttl: 604800, enabled: false },
  { extensions: 'pdf,doc,docx,xls,xlsx,zip,rar', name: '文档文件', ttl: 86400, enabled: false }
];

// 内联 monospace 代码样式
const CODE_STYLE = {
  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  background: 'rgba(0,0,0,0.06)',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: '0.85em'
};

// 根据 query 模式计算示例请求的 query 部分(纯前端演示,不落库)
function computeSampleQueryPart(mode, params, pairs) {
  if (mode === 'ignore') return '';
  let list = pairs;
  if (mode === 'whitelist') {
    list = params.length ? pairs.filter(([k]) => params.includes(k)) : [];
  } else if (mode === 'blacklist') {
    list = params.length ? pairs.filter(([k]) => !params.includes(k)) : pairs;
  }
  if (mode === 'sorted') {
    list = [...list].sort((a, b) => a[0].localeCompare(b[0]));
  }
  return list.map(([k, v]) => `${k}=${v}`).join('&');
}

// 缓存键预览:根据当前 query 模式 + vary 设置动态展示缓存键组成
function CacheKeyPreview({ queryMode, queryParams, cacheKey, isMobile }) {
  // resolved 在边缘上等同 original(已从选项去掉,旧配置按 original 显示)
  const hostMap = {
    original: 'host (访问域名)',
    resolved: 'host (访问域名)',
    host_header: 'host (回源 Host)'
  };
  const params = (queryParams || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const segments = [
    { key: 'scheme', label: 'scheme', color: 'geekblue', desc: '请求协议 http / https' },
    {
      key: 'host',
      label: hostMap[cacheKey.cache_host_mode] || 'host',
      color: 'blue',
      desc: '域名(由上面的「缓存主机模式」决定)'
    },
    { key: 'path', label: 'path', color: 'blue', desc: 'URL 路径,如 /blog/post' }
  ];

  let q;
  switch (queryMode) {
    case 'ignore':
      q = { label: '—(无 query)', color: 'default', desc: '忽略全部查询参数:?a=3 与 ?a=4 命中同一份缓存' };
      break;
    case 'sorted':
      q = { label: 'query (排序)', color: 'green', desc: '参数按名排序后全部纳入:?b=2&a=1 与 ?a=1&b=2 命中同一份,但 ?a=3 与 ?a=4 仍是两份' };
      break;
    case 'whitelist':
      q = {
        label: params.length ? `query (仅 ${params.join(', ')})` : 'query (白名单为空 → 等同忽略)',
        color: 'cyan',
        desc: '只有白名单内的参数进入缓存键,其余忽略'
      };
      break;
    case 'blacklist':
      q = {
        label: params.length ? `query (排除 ${params.join(', ')})` : 'query (完整)',
        color: 'orange',
        desc: '黑名单内的参数从缓存键剔除,其余保留'
      };
      break;
    default:
      q = { label: 'query (完整)', color: 'blue', desc: '原样保留全部参数:?a=3 与 ?a=4 是两份不同缓存' };
  }
  segments.push({ key: 'query', ...q });

  if (cacheKey.vary_by_device) segments.push({ key: 'device', label: 'device', color: 'purple', desc: '移动端 / 桌面端分别缓存' });
  if (cacheKey.vary_by_country) segments.push({ key: 'country', label: 'country', color: 'purple', desc: '按访客国家 / 地区分别缓存' });
  if (cacheKey.vary_by_language) segments.push({ key: 'lang', label: 'lang', color: 'purple', desc: '按 Accept-Language 分别缓存' });

  // 具体示例:https://example.com/blog?a=3&utm_source=ad&b=2
  const samplePairs = [['a', '3'], ['utm_source', 'ad'], ['b', '2']];
  const sampleQuery = computeSampleQueryPart(queryMode, params, samplePairs);
  const varySuffix = [
    cacheKey.vary_by_device ? 'device=mobile' : null,
    cacheKey.vary_by_country ? 'country=US' : null,
    cacheKey.vary_by_language ? 'lang=zh' : null
  ].filter(Boolean);
  const sampleKey =
    `https|example.com|/blog` +
    (sampleQuery ? `?${sampleQuery}` : '') +
    (varySuffix.length ? `|${varySuffix.join('|')}` : '');

  return (
    <div
      style={{
        marginTop: 8,
        marginBottom: 16,
        padding: isMobile ? 12 : 16,
        border: '1px dashed #91caff',
        borderRadius: 8,
        background: 'rgba(230,247,255,0.35)'
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        <Space>
          <KeyOutlined />
          缓存键预览
        </Space>
      </div>
      <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: isMobile ? 12 : 13 }}>
        在当前设置下,一个请求会按下列各部分拼出唯一缓存键;只要任一部分不同,就是两份独立的缓存。
      </div>

      {/* 公式:各段用 + 连接 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        {segments.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <span style={{ color: '#bfbfbf' }}>+</span>}
            <Tag color={s.color} style={{ margin: 0 }}>{s.label}</Tag>
          </React.Fragment>
        ))}
      </div>

      {/* 逐段说明 */}
      <div style={{ marginBottom: 12 }}>
        {segments.map((s) => (
          <div key={s.key} style={{ fontSize: isMobile ? 12 : 13, lineHeight: '22px', marginBottom: 2 }}>
            <Tag color={s.color} style={{ marginRight: 6 }}>{s.label}</Tag>
            <span style={{ color: '#8c8c8c' }}>{s.desc}</span>
          </div>
        ))}
      </div>

      {/* 具体示例 */}
      <div style={{ fontSize: isMobile ? 12 : 13, marginBottom: 6 }}>
        <span style={{ color: '#8c8c8c' }}>示例请求 </span>
        <code style={CODE_STYLE}>https://example.com/blog?a=3&utm_source=ad&b=2</code>
        <span style={{ color: '#8c8c8c' }}> 的实际缓存键:</span>
      </div>
      <code style={{ ...CODE_STYLE, display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-all', padding: '8px 10px' }}>
        {sampleKey}
      </code>

      <Alert
        style={{ marginTop: 12 }}
        type="info"
        showIcon
        message={
          <span style={{ fontSize: isMobile ? 12 : 13 }}>
            调试提示:请求带上 <code style={CODE_STYLE}>X-Debug-Cache: 1</code> 头,响应会返回 <code style={CODE_STYLE}>X-Cache-Key</code> 头,即可查看边缘节点实际使用的缓存键。(该调试头由边缘实现)
          </span>
        }
      />
    </div>
  );
}

export default function CacheSettings() {
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeText, setPurgeText] = useState('');
  const [form] = Form.useForm();
  const [fileTypeRules, setFileTypeRules] = useState(DEFAULT_FILE_TYPE_RULES);
  const [cacheKey, setCacheKey] = useState(DEFAULT_CACHE_KEY);
  // 高级缓存 / 负缓存 (status_ttl 存为对象,编辑用数组)
  const [statusTtlRules, setStatusTtlRules] = useState([]);
  // Origin Shield 回源盾 → settings.origin_shield
  const [originShield, setOriginShield] = useState({
    max_concurrency: 0,
    action: [],
    queue_max: 0,
    queue_wait_ms: 0,
    retry_after: 0
  });
  const [queryParams, setQueryParams] = useState('');

  useEffect(() => {
    if (domainData) {
      const savedRules = domainData.cache_file_types
        ? (typeof domainData.cache_file_types === 'string'
          ? JSON.parse(domainData.cache_file_types)
          : domainData.cache_file_types)
        : []; // 未配置 = 边缘无扩展名规则;推荐规则需用户点选后保存才生效

      setFileTypeRules(savedRules);

      const savedCacheKey = domainData.settings?.cache_key || DEFAULT_CACHE_KEY;
      setCacheKey({
        vary_by_device: savedCacheKey.vary_by_device || false,
        vary_by_country: savedCacheKey.vary_by_country || false,
        vary_by_language: savedCacheKey.vary_by_language || false,
        cache_host_mode: savedCacheKey.cache_host_mode || 'original',
        vary_headers: savedCacheKey.vary_headers || [],
        // 高级缓存 / 负缓存
        min_ttl: savedCacheKey.min_ttl ?? 0,
        max_ttl: savedCacheKey.max_ttl ?? 0,
        stale_if_error: savedCacheKey.stale_if_error ?? 0,
        bypass_auth: !!savedCacheKey.bypass_auth,
        bypass_cookies: Array.isArray(savedCacheKey.bypass_cookies) ? savedCacheKey.bypass_cookies : [],
        respect_client_no_cache: savedCacheKey.respect_client_no_cache !== undefined ? !!savedCacheKey.respect_client_no_cache : false
      });

      // status_ttl 对象 {"404":30} → 编辑用数组 [{code,ttl}]
      const savedStatusTtl = savedCacheKey.status_ttl || {};
      setStatusTtlRules(
        Object.keys(savedStatusTtl).map((code) => ({ code: Number(code), ttl: Number(savedStatusTtl[code]) }))
      );

      // Origin Shield 回源盾 → settings.origin_shield
      const savedShield = domainData.settings?.origin_shield || {};
      setOriginShield({
        max_concurrency: savedShield.max_concurrency ?? 0,
        action: Array.isArray(savedShield.action)
          ? savedShield.action
          : (typeof savedShield.action === 'string' && savedShield.action
            ? savedShield.action.split(/[,|]/).map((s) => s.trim()).filter(Boolean)
            : []),
        queue_max: savedShield.queue_max ?? 0,
        queue_wait_ms: savedShield.queue_wait_ms ?? 0,
        retry_after: savedShield.retry_after ?? 0
      });

      const savedQueryParams = domainData.cache_query_params;
      if (savedQueryParams) {
        const params = typeof savedQueryParams === 'string'
          ? JSON.parse(savedQueryParams)
          : savedQueryParams;
        setQueryParams(Array.isArray(params) ? params.join(', ') : '');
      } else {
        setQueryParams('');
      }

      form.setFieldsValue({
        cache_enable: normSwitch(domainData.cache_enable),
        cache_expire: domainData.cache_expire || 3600,
        cache_ttl_mode: domainData.cache_ttl_mode === 'origin_only' ? 'origin' : (domainData.cache_ttl_mode || 'origin_or_default'),
        browser_cache: normSwitch(domainData.browser_cache),
        browser_cache_expire: domainData.browser_cache_expire || 7200,
        browser_cache_ttl_mode: domainData.browser_cache_ttl_mode === 'origin_only' ? 'origin_or_default' : (domainData.browser_cache_ttl_mode || 'origin_or_default'),
        cache_query_mode: domainData.cache_query_mode || 'standard',
        stale_cache_enabled: domainData.stale_cache_enabled !== undefined
          ? !!domainData.stale_cache_enabled
          : true
      });
    }
  }, [domainData, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const invalidRules = fileTypeRules.filter(rule =>
        rule.enabled && (!rule.extensions || rule.extensions.trim() === '')
      );

      if (invalidRules.length > 0) {
        message.error('启用的规则必须填写文件扩展名');
        setLoading(false);
        return;
      }

      const validRules = fileTypeRules.filter(rule =>
        rule.extensions && rule.extensions.trim() !== ''
      );

      const queryParamsArray = queryParams
        ? queryParams.split(',').map(p => p.trim()).filter(p => p)
        : [];

      // status_ttl 数组 → 对象 {"404":30}
      const statusTtlObj = {};
      statusTtlRules.forEach((r) => {
        const code = parseInt(r.code, 10);
        if (code >= 100 && code <= 599 && r.ttl != null && r.ttl !== '') {
          statusTtlObj[String(code)] = Number(r.ttl);
        }
      });

      const cacheKeySettings = {
        ...cacheKey,
        vary_headers: cacheKey.vary_headers || [],
        status_ttl: statusTtlObj,
        bypass_cookies: cacheKey.bypass_cookies || []
      };

      const response = await cdnService.updateDomain({
        id: domainData.id,
        cache_enable: values.cache_enable,
        cache_expire: values.cache_expire,
        cache_ttl_mode: values.cache_ttl_mode,
        browser_cache: values.browser_cache,
        browser_cache_expire: values.browser_cache_expire,
        browser_cache_ttl_mode: values.browser_cache_ttl_mode,
        cache_file_types: JSON.stringify(validRules),
        cache_query_mode: values.cache_query_mode,
        cache_query_params: JSON.stringify(queryParamsArray),
        stale_cache_enabled: values.stale_cache_enabled,
        settings: {
          ...domainData.settings,
          cache_key: cacheKeySettings,
          origin_shield: originShield
        }
      });

      if (response.success) {
        message.success('缓存设置更新成功');
        reloadDomain();
      } else {
        message.error(response.error || '更新失败');
      }
    } catch (error) {
      console.error('Failed to update cache settings:', error);
      message.error('更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleFileTypeRuleChange = (index, field, value) => {
    const newRules = [...fileTypeRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setFileTypeRules(newRules);
  };

  const handleAddFileTypeRule = () => {
    setFileTypeRules([
      ...fileTypeRules,
      { extensions: '', name: '自定义', ttl: 3600, enabled: true }
    ]);
  };

  const handleDeleteFileTypeRule = (index) => {
    const newRules = fileTypeRules.filter((_, i) => i !== index);
    setFileTypeRules(newRules);
  };

  const handleCacheKeyChange = (field, value) => {
    setCacheKey(prev => ({ ...prev, [field]: value }));
  };

  // Origin Shield 回源盾更新
  const handleOriginShieldChange = (field, value) => {
    setOriginShield(prev => ({ ...prev, [field]: value }));
  };

  // 负缓存 status_ttl 行编辑
  const handleStatusTtlChange = (index, field, value) => {
    setStatusTtlRules(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const handleAddStatusTtl = () => setStatusTtlRules(prev => [...prev, { code: 404, ttl: 60 }]);
  const handleDeleteStatusTtl = (index) => setStatusTtlRules(prev => prev.filter((_, i) => i !== index));

  const fileTypeColumns = [
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled, record, index) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleFileTypeRuleChange(index, 'enabled', checked)}
        />
      )
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (name, record, index) => (
        <Input
          value={name}
          onChange={(e) => handleFileTypeRuleChange(index, 'name', e.target.value)}
          placeholder="规则名称"
        />
      )
    },
    {
      title: '文件扩展名',
      dataIndex: 'extensions',
      key: 'extensions',
      render: (extensions, record, index) => (
        <Input
          value={extensions}
          onChange={(e) => handleFileTypeRuleChange(index, 'extensions', e.target.value)}
          placeholder="用逗号分隔，如: jpg,png,gif"
        />
      )
    },
    {
      title: '缓存时间(秒)',
      dataIndex: 'ttl',
      key: 'ttl',
      width: 150,
      render: (ttl, record, index) => (
        <InputNumber
          value={ttl}
          onChange={(value) => handleFileTypeRuleChange(index, 'ttl', value)}
          min={0}
          max={31536000}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record, index) => (
        <Popconfirm
          title="确定删除此规则？"
          onConfirm={() => handleDeleteFileTypeRule(index)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const runPurge = async (paths) => {
    if (!domainData?.id) {
      message.error('缺少域名 ID');
      return false;
    }
    setPurging(true);
    try {
      // 后端只是入队（后台投递到各节点），不代表已清除完成。
      const response = await cdnService.purgeDomainCache(domainData.id, paths);
      const data = response.data || {};
      const failed = data.nodes_failed || [];
      if (response.success && failed.length === 0) {
        message.success('已提交清除，约 10 秒内在全部节点生效');
        return true;
      }
      if (data.nodes_total === 0) {
        message.error('清除失败：没有活跃的 CDN 节点');
      } else if (failed.length > 0) {
        message.warning({ content: `部分节点未响应：${failed.join('、')}`, duration: 8 });
        return true;
      } else {
        message.error(response.error || '清除失败');
      }
      return false;
    } catch (error) {
      message.error('清除失败：' + (error?.response?.data?.error || error.message || 'unknown'));
      return false;
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeCache = () => runPurge([]);

  const handlePurgeUrls = async () => {
    const lines = purgeText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      message.warning('请输入至少一条 URL 或路径');
      return;
    }
    if (lines.length > 100) {
      message.warning('一次最多 100 条');
      return;
    }
    // 后端：多条里含 * 会退化成整站清除，这里直接拦住。
    if (lines.some((l) => l.includes('*')) && lines.length > 1) {
      message.warning('前缀清除（带 *）一次只能提交一条');
      return;
    }
    if (lines.some((l) => !/^https?:\/\//i.test(l) && !l.startsWith('/'))) {
      message.warning('每行需以 / 或 http(s):// 开头');
      return;
    }
    if (await runPurge(lines)) {
      setPurgeModalOpen(false);
      setPurgeText('');
    }
  };

  const renderMobileFileTypeRule = (rule, index) => (
    <Card
      key={index}
      size="small"
      style={{ marginBottom: 8 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Switch
            checked={rule.enabled}
            onChange={(checked) => handleFileTypeRuleChange(index, 'enabled', checked)}
            size="small"
          />
          <Input
            value={rule.name}
            onChange={(e) => handleFileTypeRuleChange(index, 'name', e.target.value)}
            placeholder="规则名称"
            size="small"
            style={{ width: '60%', marginLeft: 8 }}
          />
          <Popconfirm
            title="确定删除？"
            onConfirm={() => handleDeleteFileTypeRule(index)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </div>
      }
    >
      <Form.Item label="扩展名" style={{ marginBottom: 8 }}>
        <Input
          value={rule.extensions}
          onChange={(e) => handleFileTypeRuleChange(index, 'extensions', e.target.value)}
          placeholder="jpg,png,gif"
          size="small"
        />
      </Form.Item>
      <Form.Item label="缓存时间" style={{ marginBottom: 0 }}>
        <InputNumber
          value={rule.ttl}
          onChange={(value) => handleFileTypeRuleChange(index, 'ttl', value)}
          min={0}
          max={31536000}
          size="small"
          style={{ width: '100%' }}
          addonAfter="秒"
        />
      </Form.Item>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>缓存设置</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        extra={
          <Space size={isMobile ? 4 : 8}>
            <CachePresetTexts size={isMobile ? 'small' : 'middle'} />
            <Button
              onClick={() => setPurgeModalOpen(true)}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '按 URL' : '按 URL 清除'}
            </Button>
            <Popconfirm
              title="清除该域名全部缓存？"
              description="清除后短时间内回源请求会增加"
              onConfirm={handlePurgeCache}
              okText="清除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
            >
              <Button
                danger
                icon={<ReloadOutlined />}
                loading={purging}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? '全站' : '清除全站缓存'}
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Alert
          message="缓存配置说明"
          description={isMobile
            ? "合理配置缓存策略可以提升性能并减少源站负载。"
            : "合理配置缓存策略可以显著提升网站性能并减少源站负载。边缘缓存存储在 CDN 节点上，浏览器缓存存储在用户浏览器中。"
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: isMobile ? 16 : 24 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            cache_enable: 'auto',
            cache_expire: 3600,
            cache_ttl_mode: 'origin_or_default',
            browser_cache: 'auto',
            browser_cache_expire: 7200,
            browser_cache_ttl_mode: 'origin_or_default',
            cache_query_mode: 'standard'
          }}
        >
          <Divider>边缘缓存设置</Divider>

          <Alert
            message="边缘缓存"
            description="CDN 节点上缓存您的内容，减少回源请求，加快访问速度。"
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="cache_enable"
            label="边缘缓存"
            extra="控制 CDN 是否缓存您的内容"
          >
            <Select>
              <Option value="auto">
                <Space><Tag color="blue">自动</Tag><span>根据响应头自动判断</span></Space>
              </Option>
              <Option value="on">
                <Space><Tag color="green">启用</Tag><span>强制启用缓存</span></Space>
              </Option>
              <Option value="off">
                <Space><Tag color="red">禁用</Tag><span>不缓存内容</span></Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}>
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;
              return (
                <Form.Item
                  name="stale_cache_enabled"
                  label={
                    <Space>
                      <span>源站故障时返回过期缓存</span>
                      <Tooltip title="当源站不可用（超时、500/502/503/504错误）时，返回已过期的缓存内容而不是错误页面">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                  extra="启用后，即使缓存已过期，在源站故障时也会返回缓存内容，提高可用性"
                >
                  <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable || prev.cache_ttl_mode !== curr.cache_ttl_mode}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              const ttlMode = getFieldValue('cache_ttl_mode');
              if (cacheEnabled === 'off') return null;

              const ttlLabel = ttlMode === 'fixed' ? '边缘缓存时间（秒）' : ttlMode === 'origin' ? '回退缓存时间（秒）' : '默认/配置缓存时间（秒）';
              const ttlExtra = ttlMode === 'fixed' ? '始终使用此缓存时间' : ttlMode === 'origin' ? '当源站没有返回 Cache-Control 时使用此值' : ttlMode === 'origin_or_default' ? '当源站没有返回 Cache-Control 时使用此默认值' : '与源站 TTL 进行比较的配置值';

              return (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="cache_ttl_mode"
                        label={<Space><span>缓存时间模式</span><Tooltip title="控制如何确定缓存过期时间"><QuestionCircleOutlined /></Tooltip></Space>}
                      >
                        <Select>
                          <Option value="origin_or_default"><Space><Tag color="blue">跟随源站或默认</Tag><span>优先源站，无则用配置值</span></Space></Option>
                          <Option value="fixed"><Space><Tag color="green">固定</Tag><span>始终使用配置的过期时间</span></Space></Option>
                          <Option value="origin"><Space><Tag color="cyan">跟随源站</Tag><span>完全遵循源站 Cache-Control</span></Space></Option>
                          <Option value="min"><Space><Tag color="orange">取最小值</Tag><span>源站与配置取较小值</span></Space></Option>
                          <Option value="max"><Space><Tag color="purple">取最大值</Tag><span>源站与配置取较大值</span></Space></Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="cache_expire" label={ttlLabel} extra={ttlExtra}>
                        <InputNumber min={0} max={31536000} style={{ width: '100%' }} addonAfter="秒" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <div style={{ marginBottom: 16 }}>
                    <Space wrap size={[8, 8]}>
                      <Tag color="cyan">推荐值:</Tag>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 3600 })}>1小时</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 86400 })}>1天</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ cache_expire: 604800 })}>7天</Button>
                    </Space>
                  </div>
                </>
              );
            }}
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}>
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;
              return (
                <>
                  <Divider>按文件类型缓存</Divider>
                  <Alert
                    message="文件类型缓存规则"
                    description="为不同类型的文件设置不同的缓存时间。这些规则会覆盖全局缓存设置。静态资源（图片、CSS、JS）通常可以设置更长的缓存时间。"
                    type="info" showIcon style={{ marginBottom: 16 }}
                  />
                  {isMobile ? (
                    <div style={{ marginBottom: 16 }}>
                      {fileTypeRules.map((rule, index) => renderMobileFileTypeRule(rule, index))}
                    </div>
                  ) : (
                    <Table
                      columns={fileTypeColumns}
                      dataSource={fileTypeRules}
                      pagination={false}
                      size="small"
                      rowKey={(record, index) => index}
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {fileTypeRules.length === 0 && (
                    <Button type="link" onClick={() => setFileTypeRules(DEFAULT_FILE_TYPE_RULES)} style={{ padding: 0, marginBottom: 8 }}>
                      填入推荐规则
                    </Button>
                  )}
                  <Button type="dashed" onClick={handleAddFileTypeRule} icon={<PlusOutlined />} block style={{ marginBottom: 24 }}>
                    添加文件类型规则
                  </Button>
                </>
              );
            }}
          </Form.Item>

          <Divider>浏览器缓存设置</Divider>

          <Alert message="浏览器缓存" description="控制用户浏览器缓存内容的时间，减少重复请求。" type="success" showIcon style={{ marginBottom: 16 }} />

          <Form.Item name="browser_cache" label="浏览器缓存" extra="控制浏览器是否缓存您的内容">
            <Select>
              <Option value="auto"><Space><Tag color="blue">自动</Tag><span>根据响应头自动判断</span></Space></Option>
              <Option value="on"><Space><Tag color="green">启用</Tag><span>强制启用浏览器缓存</span></Space></Option>
              <Option value="off"><Space><Tag color="red">禁用</Tag><span>禁止浏览器缓存</span></Space></Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.browser_cache !== curr.browser_cache || prev.browser_cache_ttl_mode !== curr.browser_cache_ttl_mode}
          >
            {({ getFieldValue }) => {
              const browserCacheEnabled = getFieldValue('browser_cache');
              const ttlMode = getFieldValue('browser_cache_ttl_mode');
              if (browserCacheEnabled === 'off') return null;

              const ttlLabel = ttlMode === 'fixed' ? '浏览器缓存时间（秒）' : ttlMode === 'origin' ? '回退缓存时间（秒）' : '默认/配置缓存时间（秒）';
              const ttlExtra = ttlMode === 'fixed' ? '始终使用此缓存时间' : ttlMode === 'origin' ? '当源站没有返回 Cache-Control 时使用此值' : ttlMode === 'origin_or_default' ? '当源站没有返回 Cache-Control 时使用此默认值' : '与源站 TTL 进行比较的配置值';

              return (
                <>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="browser_cache_ttl_mode"
                        label={<Space><span>浏览器缓存时间模式</span><Tooltip title="控制如何确定浏览器缓存过期时间"><QuestionCircleOutlined /></Tooltip></Space>}
                      >
                        <Select>
                          <Option value="origin_or_default"><Space><Tag color="blue">跟随源站或默认</Tag><span>优先源站，无则用配置值</span></Space></Option>
                          <Option value="fixed"><Space><Tag color="green">固定</Tag><span>始终使用配置的过期时间</span></Space></Option>
                          <Option value="origin"><Space><Tag color="cyan">跟随源站</Tag><span>完全遵循源站 Cache-Control</span></Space></Option>
                          <Option value="min"><Space><Tag color="orange">取最小值</Tag><span>源站与配置取较小值</span></Space></Option>
                          <Option value="max"><Space><Tag color="purple">取最大值</Tag><span>源站与配置取较大值</span></Space></Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="browser_cache_expire" label={ttlLabel} extra={ttlExtra}>
                        <InputNumber min={0} max={31536000} style={{ width: '100%' }} addonAfter="秒" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <div style={{ marginBottom: 16 }}>
                    <Space wrap size={[8, 8]}>
                      <Tag color="cyan">推荐值:</Tag>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 7200 })}>2小时</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 86400 })}>1天</Button>
                      <Button size="small" onClick={() => form.setFieldsValue({ browser_cache_expire: 604800 })}>7天</Button>
                    </Space>
                  </div>
                </>
              );
            }}
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable || prev.cache_query_mode !== curr.cache_query_mode}
          >
            {({ getFieldValue }) => {
              const cacheEnabled = getFieldValue('cache_enable');
              if (cacheEnabled === 'off') return null;
              const queryMode = getFieldValue('cache_query_mode');
              return (
                <>
                  <Divider><Space><KeyOutlined />缓存键设置</Space></Divider>
                  <Alert
                    message="缓存键配置"
                    description="缓存键决定了如何区分不同的缓存版本。合理配置可以提高缓存命中率，同时确保用户获得正确的内容。"
                    type="info" showIcon style={{ marginBottom: 16 }}
                  />
                  <Form.Item
                    name="cache_query_mode"
                    label={<Space><span>Query String处理模式</span><Tooltip title="控制URL中的查询字符串如何影响缓存键"><QuestionCircleOutlined /></Tooltip></Space>}
                    extra="控制URL中的?key=value部分如何影响缓存"
                  >
                    <Select>
                      <Option value="standard"><Space><Tag color="blue">标准</Tag><span>完整保留所有查询参数</span></Space></Option>
                      <Option value="ignore"><Space><Tag color="orange">忽略</Tag><span>完全忽略查询字符串</span></Space></Option>
                      <Option value="sorted"><Space><Tag color="green">排序</Tag><span>按参数名排序后缓存（推荐）</span></Space></Option>
                      <Option value="whitelist"><Space><Tag color="cyan">白名单</Tag><span>只保留指定的参数</span></Space></Option>
                      <Option value="blacklist"><Space><Tag color="red">黑名单</Tag><span>排除指定的参数</span></Space></Option>
                    </Select>
                  </Form.Item>
                  {(queryMode === 'whitelist' || queryMode === 'blacklist') && (
                    <Form.Item
                      label={queryMode === 'whitelist' ? 'Query参数白名单' : 'Query参数黑名单'}
                      extra="多个参数用逗号分隔，如: utm_source, utm_medium, ref"
                    >
                      <Input
                        value={queryParams}
                        onChange={(e) => setQueryParams(e.target.value)}
                        placeholder={queryMode === 'whitelist' ? '输入要保留的参数名' : '输入要排除的参数名'}
                      />
                    </Form.Item>
                  )}
                  <Row gutter={[16, 16]}>
                    <Col xs={12} sm={8}>
                      <Form.Item label={<Space><span>{isMobile ? '按设备' : '按设备类型区分'}</span><Tooltip title="为移动端和桌面端分别缓存不同的版本"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={cacheKey.vary_by_device} onChange={(checked) => handleCacheKeyChange('vary_by_device', checked)} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Form.Item label={<Space><span>{isMobile ? '按国家' : '按国家区分'}</span><Tooltip title="根据访问者所在国家/地区缓存不同版本"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={cacheKey.vary_by_country} onChange={(checked) => handleCacheKeyChange('vary_by_country', checked)} />
                      </Form.Item>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Form.Item label={<Space><span>{isMobile ? '按语言' : '按语言区分'}</span><Tooltip title="根据Accept-Language请求头缓存不同语言版本"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={cacheKey.vary_by_language} onChange={(checked) => handleCacheKeyChange('vary_by_language', checked)} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label={<Space><span>缓存主机模式</span><Tooltip title="决定缓存键中使用哪个主机名"><QuestionCircleOutlined /></Tooltip></Space>}
                    extra="控制缓存键中使用的域名来源"
                  >
                    <Select value={cacheKey.cache_host_mode === 'resolved' ? 'original' : cacheKey.cache_host_mode} onChange={(value) => handleCacheKeyChange('cache_host_mode', value)}>
                      <Option value="original"><Space><Tag color="blue">访问域名</Tag><span>每个主机名各自缓存</span></Space></Option>
                      <Option value="host_header"><Space><Tag color="cyan">回源 Host</Tag><span>回源 Host 相同的主机名共用一份缓存</span></Space></Option>
                    </Select>
                  </Form.Item>

                  <CacheKeyPreview
                    queryMode={queryMode}
                    queryParams={queryParams}
                    cacheKey={cacheKey}
                    isMobile={isMobile}
                  />
                </>
              );
            }}
          </Form.Item>

          {/* 高级缓存 / 负缓存 —— settings.cache_key.{status_ttl,min_ttl,max_ttl,stale_if_error,bypass_*} */}
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.cache_enable !== curr.cache_enable}
          >
            {({ getFieldValue }) => {
              if (getFieldValue('cache_enable') === 'off') return null;
              return (
                <>
                  <Divider>高级缓存 / 负缓存</Divider>

                  <Alert
                    message="负缓存与 TTL 边界"
                    description="按响应状态码设置缓存时间（负缓存），并对最终缓存时长设置上下限；源站故障时可在宽限窗口内继续返回过期缓存。"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div style={{ marginBottom: 4, fontWeight: 500 }}>按状态码缓存（负缓存）</div>
                  <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: isMobile ? 12 : 13 }}>
                    为特定 HTTP 状态码（如 404、500、301）单独设置缓存秒数，优先级高于其他缓存时间设置。缓存 404/500 可避免源站错误被反复回源。
                  </div>
                  {statusTtlRules.map((rule, index) => (
                    <Row gutter={8} key={index} style={{ marginBottom: 8 }} align="middle">
                      <Col xs={11} sm={8}>
                        <InputNumber
                          value={rule.code}
                          onChange={(v) => handleStatusTtlChange(index, 'code', v)}
                          min={100}
                          max={599}
                          placeholder="状态码"
                          style={{ width: '100%' }}
                          addonBefore="状态码"
                        />
                      </Col>
                      <Col xs={9} sm={8}>
                        <InputNumber
                          value={rule.ttl}
                          onChange={(v) => handleStatusTtlChange(index, 'ttl', v)}
                          min={0}
                          max={31536000}
                          placeholder="TTL"
                          style={{ width: '100%' }}
                          addonAfter="秒"
                        />
                      </Col>
                      <Col xs={4}>
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteStatusTtl(index)} />
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={handleAddStatusTtl} icon={<PlusOutlined />} block style={{ marginBottom: 16 }}>
                    添加状态码规则
                  </Button>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>最小缓存时间 (min_ttl)</span><Tooltip title="缓存时间不低于此值(0 表示不限制),仅抬高大于 0 的 TTL"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <InputNumber value={cacheKey.min_ttl} onChange={(v) => handleCacheKeyChange('min_ttl', v ?? 0)} min={0} max={31536000} style={{ width: '100%' }} addonAfter="秒" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>最大缓存时间 (max_ttl)</span><Tooltip title="缓存时间不超过此值(0 表示不限制)"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <InputNumber value={cacheKey.max_ttl} onChange={(v) => handleCacheKeyChange('max_ttl', v ?? 0)} min={0} max={31536000} style={{ width: '100%' }} addonAfter="秒" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>故障宽限 (stale_if_error)</span><Tooltip title="源站故障时,在此秒数窗口内继续返回过期缓存"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <InputNumber value={cacheKey.stale_if_error} onChange={(v) => handleCacheKeyChange('stale_if_error', v ?? 0)} min={0} max={31536000} style={{ width: '100%' }} addonAfter="秒" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left" style={{ margin: '4px 0 12px' }}>缓存绕过</Divider>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>带 Authorization 不缓存</span><Tooltip title="请求带有 Authorization 头时跳过缓存,直接回源"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={!!cacheKey.bypass_auth} onChange={(c) => handleCacheKeyChange('bypass_auth', c)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>尊重客户端 no-cache</span><Tooltip title="客户端请求带 Cache-Control: no-cache 时跳过缓存"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Switch checked={cacheKey.respect_client_no_cache === true} onChange={(c) => handleCacheKeyChange('respect_client_no_cache', c)} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                      <Form.Item label={<Space><span>绕过缓存的会话 Cookie</span><Tooltip title="命中这些 Cookie 名时跳过缓存(如 session、token)"><QuestionCircleOutlined /></Tooltip></Space>}>
                        <Select mode="tags" value={cacheKey.bypass_cookies || []} onChange={(v) => handleCacheKeyChange('bypass_cookies', v)} placeholder="Cookie 名(回车分隔)" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              );
            }}
          </Form.Item>

          {/* 回源防护 / Origin Shield —— settings.origin_shield.* */}
          <Divider>回源防护 / Origin Shield</Divider>
          <Alert
            message="源站保护"
            description="限制单域名回源并发,超过阈值时按阶梯动作保护源站:优先返回缓存 → 排队等待 → 人机挑战 → 直接拦截。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item label={<Space><span>回源最大并发</span><Tooltip title="单域名到源站的最大并发请求数,0 表示不限制"><QuestionCircleOutlined /></Tooltip></Space>}>
                <InputNumber value={originShield.max_concurrency} onChange={(v) => handleOriginShieldChange('max_concurrency', v ?? 0)} min={0} max={1000000} style={{ width: '100%' }} placeholder="0 = 不限" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={<Space><span>超限动作(阶梯)</span><Tooltip title="超过并发上限时按顺序执行的动作阶梯"><QuestionCircleOutlined /></Tooltip></Space>}>
                <Select
                  mode="multiple"
                  value={originShield.action}
                  onChange={(v) => handleOriginShieldChange('action', v)}
                  placeholder="选择动作阶梯"
                  style={{ width: '100%' }}
                  options={[
                    { value: 'cache_first', label: 'cache_first（优先返回缓存）' },
                    { value: 'queue', label: 'queue（排队等待）' },
                    { value: 'challenge', label: 'challenge（人机挑战）' },
                    { value: 'block', label: 'block（直接拦截）' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>队列长度上限</span><Tooltip title="queue 动作下最多排队的请求数"><QuestionCircleOutlined /></Tooltip></Space>}>
                <InputNumber value={originShield.queue_max} onChange={(v) => handleOriginShieldChange('queue_max', v ?? 0)} min={0} max={1000000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>队列等待 (ms)</span><Tooltip title="queue 动作下单个请求的最长等待毫秒数"><QuestionCircleOutlined /></Tooltip></Space>}>
                <InputNumber value={originShield.queue_wait_ms} onChange={(v) => handleOriginShieldChange('queue_wait_ms', v ?? 0)} min={0} max={600000} style={{ width: '100%' }} addonAfter="ms" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={<Space><span>Retry-After (秒)</span><Tooltip title="block / challenge 时返回给客户端的 Retry-After 秒数"><QuestionCircleOutlined /></Tooltip></Space>}>
                <InputNumber value={originShield.retry_after} onChange={(v) => handleOriginShieldChange('retry_after', v ?? 0)} min={0} max={86400} style={{ width: '100%' }} addonAfter="秒" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} size="large">保存设置</Button>
          </Form.Item>
        </Form>
      </Card>

      <Modal
        title="按 URL / 前缀清除"
        open={purgeModalOpen}
        onOk={handlePurgeUrls}
        onCancel={() => setPurgeModalOpen(false)}
        okText="提交清除"
        cancelText="取消"
        confirmLoading={purging}
        destroyOnClose
      >
        <Input.TextArea
          value={purgeText}
          onChange={(e) => setPurgeText(e.target.value)}
          rows={8}
          placeholder={'每行一条，例如：\n/index.html\nhttps://www.example.com/app.js?v=2\n/static/*'}
        />
        <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
          以 * 结尾为前缀清除（一次一条）；不带参数的路径会清除其全部参数变体。
        </div>
      </Modal>
    </div>
  );
}
